import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { IAssistantMessage } from "@/features/assistant/types";
import { useGrillAnswers } from "@/features/assistant/hooks/useGrillAnswers";
import { sashOverlayApi, sashOverlayMultiFrameApi, getTaskStatusApi, fetchResultAsBlobUrl } from "../api";
import {
  urlToFile,
  buildResultUrl,
  createTransparentPngFile,
} from "../utils/imageHelpers";
import { ISashOverlayRequest, ISashOverlayMultiFrameRequest, ITaskStatus } from "../types";

const POLL_INTERVAL_MS = 1000;
const DEBOUNCE_MS = 300;

interface GrillBlendState {
  isApplying: boolean;
  grillImageUrl: string | null;
  lastTaskStatus: ITaskStatus | null;
  error: string | null;
}

/**
 * Polls a sash-overlay task until SUCCESS or FAILURE.
 * Returns the full result URL on success.
 */
const pollUntilDone = async (
  taskId: string,
  isCancelled: () => boolean,
): Promise<string> => {
  while (true) {
    if (isCancelled()) throw new Error("cancelled");

    const status = await getTaskStatusApi(taskId);

    if (status.state === "SUCCESS") {
      if (!status.result_url)
        throw new Error("Task succeeded but result_url is missing");
      return buildResultUrl(status.result_url);
    }

    if (status.state === "FAILURE") {
      throw new Error(status.error || `Task ${taskId} failed`);
    }

    // PENDING / PROCESSING — wait then retry
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
};

/**
 * Web grill blending hook.
 *
 * Single-frame window  → POST /api/sash-overlay  (one top_grid + one bottom_grid)
 * Multi-frame window   → POST /api/sash-overlay-multi-frame
 *                        (top_grids[] + bottom_grids[], one file per frame left-to-right)
 *                        Frames that are missing one side get their other side duplicated
 *                        so the counts always match.
 *
 * @param messages       - Current window's assistant messages
 * @param windowImageUrl - The window template image URL (selectedProduct?.thumbnail)
 * @param enabled        - When false, seeds the ref so no call fires until a real change occurs
 * @param color          - Hex color (e.g. from the profile color answer, via
 *                         useGetTemplateColors) to tint the decorative bars —
 *                         same tint already applied to the frame via
 *                         useColorizeWindow. Optional; omit to keep each bar
 *                         image's own baked-in color.
 */
export const useGrillBlend = (
  messages: IAssistantMessage[],
  windowImageUrl: string | null,
  enabled: boolean = true,
  color?: string | null,
  forceKey?: string | number,
  frameCount: number = 1,
) => {
  const [state, setState] = useState<GrillBlendState>({
    isApplying: false,
    grillImageUrl: null,
    lastTaskStatus: null,
    error: null,
  });

  const grillWindows = useGrillAnswers(messages, frameCount);

  // Stable key — only reflects complete {top, bottom} pairs so the effect
  // only fires when a fully-paired selection actually changes. Includes
  // color so a color-only change (existing grills, frame recolored) is
  // detected as a real change and re-triggers the blend. forceKey lets a
  // caller force a genuine re-run (e.g. when another stage in the DBR ->
  // colorize -> grill pipeline turns on/off) — folded in so it's a real
  // dependency change, not just a state reset.
  // Includes every physical frame (not just ones with a grill selected) so the
  // key reflects the full frame layout — needed because the effect below now
  // sends a positional placeholder for "None" frames in multi-frame requests.
  const grillKey = useMemo(
    () =>
      grillWindows
        .map(
          (w) =>
            `${w.windowLabel}:${w.topSash?.optionId ?? "none"}:${w.bottomSash?.optionId ?? "none"}`,
        )
        .join("|") + `::color:${color ?? "none"}::force:${forceKey ?? ""}`,
    [grillWindows, color, forceKey],
  );

  const grillWindowsRef = useRef(grillWindows);
  grillWindowsRef.current = grillWindows;

  const lastAppliedRef = useRef({ grillKey: "", windowImageUrl: "" });
  // Track current blob URL so we can revoke it when a new result arrives
  const currentBlobUrlRef = useRef<string | null>(null);
  const prevEnabledRef = useRef(false);

  const sashOverlay = useMutation({
    mutationFn: (data: ISashOverlayRequest) => sashOverlayApi(data),
  });

  const sashOverlayMultiFrame = useMutation({
    mutationFn: (data: ISashOverlayMultiFrameRequest) =>
      sashOverlayMultiFrameApi(data),
  });

  useEffect(() => {
    // Not enabled — seed the ref so no call fires until a real change occurs.
    // Also clear isApplying: a previous run may have flipped it true right
    // before its debounce/network call, then gotten cancelled by this very
    // effect re-running (enabled went false) before it reached its own
    // success/error branch — the only places that normally reset it. Left
    // alone, isApplying would stay stuck true forever even though nothing is
    // actually in flight anymore.
    if (!enabled) {
      lastAppliedRef.current = {
        grillKey,
        windowImageUrl: windowImageUrl ?? "",
      };
      prevEnabledRef.current = false;
      setState((prev) => (prev.isApplying ? { ...prev, isApplying: false } : prev));
      return;
    }

    // enabled just transitioned false→true: clear the ref so the full pipeline
    // re-runs even if the key hasn't changed (e.g. color-only change with existing grills)
    const justEnabled = !prevEnabledRef.current;
    prevEnabledRef.current = true;

    // Nothing changed — skip (but not on the first enable transition)
    if (
      !justEnabled &&
      lastAppliedRef.current.grillKey === grillKey &&
      lastAppliedRef.current.windowImageUrl === (windowImageUrl ?? "")
    )
      return;

    // No grill selected or no window image — reset to DBR base image
    // (windowImageUrl). Must also clear lastTaskStatus, not just
    // grillImageUrl: downstream consumers read grillBlend.lastTaskStatus?.result_url
    // as the primary grill output, so leaving it stale here (e.g. after the
    // user re-answers an earlier question and the grill answers disappear)
    // would keep feeding an old grill-blended image into the final AI blend.
    if (!grillKey || !windowImageUrl) {
      lastAppliedRef.current = {
        grillKey,
        windowImageUrl: windowImageUrl ?? "",
      };
      // isApplying: false too — see the !enabled branch above for why a
      // previous in-flight run can leave this stuck true otherwise.
      setState((prev) => ({
        ...prev,
        isApplying: false,
        grillImageUrl: windowImageUrl ?? null,
        lastTaskStatus: null,
        error: null,
      }));
      return;
    }

    // Parent "Decorative Bar Type" answered None — useGrillAnswers returns []
    // since there's no per-sash answer to read. Build the same frameCount
    // blank-shaped entries the "every sash individually answered None" case
    // uses below, purely so both cases share one code path.
    const allWindows = grillWindowsRef.current.length > 0
      ? grillWindowsRef.current
      : Array.from({ length: Math.max(1, frameCount) }, (_, i) => ({
          windowLabel: `Window ${i + 1}`,
          topSash: null,
          bottomSash: null,
        }));

    // Frames with at least one sash selected.
    const completeWindows = allWindows.filter(
      (w) => w.topSash !== null || w.bottomSash !== null,
    );

    if (completeWindows.length === 0) {
      // No real grid anywhere (every sash answered None, or the parent
      // Decorative Bar Type itself is None). The backend's sash-overlay
      // endpoints reject a request with zero real grids ("Upload at least one
      // grid."), so there's nothing valid to send — and nothing to undo via
      // the backend either: windowImageUrl here is always the clean
      // colorize/DBR template, never a previously-grilled image (grill
      // compositing only ever happens downstream of it), so it's already the
      // correct "no grill" result. Reset directly to it.
      lastAppliedRef.current = { grillKey, windowImageUrl };
      setState((prev) => ({
        ...prev,
        isApplying: false,
        grillImageUrl: windowImageUrl ?? null,
        lastTaskStatus: null,
        error: null,
      }));
      return;
    }

    let cancelled = false;

    // Flip isApplying synchronously, before the debounce timer — otherwise
    // consumers reading isApplying/lastTaskStatus during the 300ms debounce
    // window see the PREVIOUS (stale) completed blend and treat the pipeline
    // as "settled", letting a save/add-to-cart proceed with a blend that
    // hasn't actually started for the new grill selection yet.
    setState((prev) => ({ ...prev, isApplying: true, error: null }));

    // Debounce: only fire the actual network call after 300ms of inactivity
    const timer = setTimeout(async () => {
      console.group("[useGrillBlend] 🚀 Starting sash overlay");

      try {
        const windowFile = await urlToFile(windowImageUrl, "window.png");

        if (!windowFile || windowFile.size === 0) {
          console.groupEnd();
          setState((prev) => ({ ...prev, isApplying: false }));
          return;
        }

        if (cancelled) {
          console.groupEnd();
          return;
        }

        let taskId: string;
        let resultUrl: string;

        // ── Single-frame: use /api/sash-overlay ──────────────────────────────
        // Gated on the window's TOTAL physical frame count (allWindows), not
        // on how many frames actually have a grill — a 3-frame coupled window
        // with a grill on only the middle frame must still go through the
        // multi-frame branch below, so the grill lands on the correct frame
        // instead of being overlaid across the whole (3-frame-wide) image via
        // the single-frame endpoint.
        if (allWindows.length === 1) {
          // completeWindows is guaranteed non-empty here (the all-None case
          // returns earlier above), so there's always a real sash to read.
          const win:any = completeWindows[0];

          const [topFile, bottomFile] = await Promise.all([
            win.topSash?.thumbnail
              ? urlToFile(win.topSash.thumbnail, "top_grid.png")
              : Promise.resolve(null),
            win.bottomSash?.thumbnail
              ? urlToFile(win.bottomSash.thumbnail, "bottom_grid.png")
              : Promise.resolve(null),
          ]);

          if (cancelled) {
            console.groupEnd();
            return;
          }

          const submission = await sashOverlay.mutateAsync({
            window_image: windowFile,
            top_grid: topFile,
            bottom_grid: bottomFile,
            color: color || undefined,
          });

          setState((prev) => ({ ...prev, lastTaskStatus: null }));
          taskId = submission.task_id;

          // ── Multi-frame: use /api/sash-overlay-multi-frame ────────────────────
        } else {
          // Fetch top + bottom files for EVERY physical frame, in left-to-right
          // order, so array position always matches the real frame position —
          // not just the frames that happen to have a grill. A frame with no
          // grill at all (both sides "None") gets a transparent placeholder
          // instead of being dropped, otherwise dropping it would shift every
          // later frame's grill one position to the left.
          const frameFiles = await Promise.all(
            allWindows.map(async (win, i) => {
              const [top, bottom] = await Promise.all([
                win.topSash?.thumbnail
                  ? urlToFile(win.topSash.thumbnail, `top_grid_${i}.png`)
                  : Promise.resolve(null),
                win.bottomSash?.thumbnail
                  ? urlToFile(win.bottomSash.thumbnail, `bottom_grid_${i}.png`)
                  : Promise.resolve(null),
              ]);
              if (!top && !bottom) {
                const blank = createTransparentPngFile(`blank_grid_${i}.png`);
                return { top: blank, bottom: blank };
              }
              // Fill a missing side with its own blank/transparent file — NEVER
              // with the other side's real grid. That used to duplicate the
              // present side's file onto the missing one (`top ?? bottom!` /
              // `bottom ?? top!`), so explicitly answering "None" for just one
              // side (e.g. Bottom Sash) while the other had a real style still
              // showed that real style's grill on the "None" side too, because
              // the request sent the SAME file for both.
              return {
                top: top ?? createTransparentPngFile(`blank_top_${i}.png`),
                bottom: bottom ?? createTransparentPngFile(`blank_bottom_${i}.png`),
              };
            }),
          );
          const topGrids = frameFiles.map((f) => f.top);
          const bottomGrids = frameFiles.map((f) => f.bottom);

          if (cancelled) {
            console.groupEnd();
            return;
          }

          const submission = await sashOverlayMultiFrame.mutateAsync({
            window_image: windowFile,
            top_grids: topGrids,
            bottom_grids: bottomGrids,
            color: color || undefined,
          });

          setState((prev) => ({ ...prev, lastTaskStatus: null }));
          taskId = submission.task_id;
        }

        resultUrl = await pollUntilDone(taskId, () => cancelled);

        if (!cancelled) {
          const blobUrl = await fetchResultAsBlobUrl(resultUrl);

          if (!cancelled) {
            if (currentBlobUrlRef.current?.startsWith("blob:")) {
              URL.revokeObjectURL(currentBlobUrlRef.current);
            }
            currentBlobUrlRef.current = blobUrl;

            console.groupEnd();

            lastAppliedRef.current = { grillKey, windowImageUrl };
            setState((prev) => ({
              ...prev,
              isApplying: false,
              grillImageUrl: blobUrl,
              lastTaskStatus: {
                task_id: taskId,
                state: "SUCCESS",
                result_url: resultUrl,
              },
            }));
          }
        }
      } catch (error) {
        console.groupEnd();
        if (!cancelled) {
          const msg = error instanceof Error ? error.message : String(error);
          if (msg !== "cancelled") {
            setState((prev) => ({ ...prev, isApplying: false, error: msg }));
          }
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, grillKey, windowImageUrl, color, frameCount]);

  const reset = useCallback(() => {
    if (currentBlobUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(currentBlobUrlRef.current);
      currentBlobUrlRef.current = null;
    }
    setState({
      isApplying: false,
      grillImageUrl: null,
      lastTaskStatus: null,
      error: null,
    });
    lastAppliedRef.current = { grillKey: "", windowImageUrl: "" };
    prevEnabledRef.current = false;
  }, []);

  return {
    grillImageUrl: state.grillImageUrl,
    isApplying: state.isApplying,
    lastTaskStatus: state.lastTaskStatus,
    error: state.error,
    grillWindows,
    reset,
  };
};;
